import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
// import Page from "@insite/mobius/Page"; // Custom Code
import * as React from "react";
import { ReactNode } from "react";
// import styled from "styled-components"; // Custom Code

/* Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Page, { PageProps } from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SkipNav, { SkipNavStyles } from "@store/Overrides/Components/SkipNav";
import breakpointValues from "@store/Framework/Common/Utilities/breakpointValues";
import ExperimentsSnippets from "@store/Library/Components/Injectors/ExperimentsSnippets";
import FraudProtectionFingerprint from "@store/Library/Components/Injectors/FraudProtection/FraudProtectionFingerprint";
import GoogleAnalytics from "@store/Library/Components/Injectors/GoogleAnalytics";
import SiteWrapper, { SiteWrapperPresentationProps } from "@store/Library/Components/Toolkit/SiteWrapper";
import { stretchStyles } from "@store/Themes/common/mixins/common";
import { css } from "styled-components";

/* Store Custom Code - Start */
import LiveChat from "@store/Library/Components/Injectors/LiveChat";
/* Store Custom Code - End */

export interface PageLayoutStyles {
    siteWrapper?: SiteWrapperPresentationProps;
    content?: InjectableCss;
    // Custom Code
    skipLinks?: InjectableCss;
    skipLink?: SkipNavStyles;
    header?: InjectableCss;
    breadcrumb?: PageProps;
    footer?: InjectableCss;
}

const pageLayoutStyles: PageLayoutStyles = {
    skipLinks: {
        css: css`
            position: absolute;
            top: 0;
            left: 0;
            z-index: 1000;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    skipLink: {
        skipToContent: {
            css: css`
                margin: 0.5rem;
                position: absolute;
                top: 0;
                left: 0;
                clip: rect(0, 0, 0, 0);
                clip-path: inset(50%);
                width: 1px;
                height: 1px;
                overflow: hidden;
                white-space: nowrap;

                &:focus {
                    clip: auto;
                    clip-path: none;
                    width: auto;
                    height: auto;
                    overflow: visible;
                    z-index: 1001;
                }
            `,
        },
    },
    header: {
        css: css`
            position: relative;
            z-index: 4;

            > ${StyledWrapper}:first-child:last-child {
                ${x => breakpointValues(x.theme.design.header.height, "height")}

                svg {
                    display: none; /* Disable DelayedSpinner */
                }
            }
        `,
    },
    breadcrumb: {
        css: css`
            position: relative;
            z-index: 0;

            @media print {
                display: none;
            }
        `,
    },
    content: {
        css: css`
            ${stretchStyles()}
            position: relative;
            z-index: 2;
        `,
    },
    footer: {
        css: css`
            position: relative;
            z-index: 1;
        `,
    },
};

const styles = pageLayoutStyles;
/* Custom Code - End */

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    showSignInOverlay,
    signInOverlay,
    showStorePickupOverlay,
    storePickupOverlay,
    showAddressBookOverlay,
    addressBookOverlay,
}: PageLayoutProps) => {
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const headerRef = React.useRef<HTMLDivElement | null>(null);
    const footerRef = React.useRef<HTMLDivElement | null>(null);
    const mainTargetRef = React.useRef<HTMLElement | null>(null);
    const navTargetRef = React.useRef<HTMLElement | null>(null);
    const footerTargetRef = React.useRef<HTMLElement | null>(null);

    React.useEffect(() => {
        mainTargetRef.current = contentRef.current?.querySelector("main") ?? null;
        navTargetRef.current = headerRef.current?.querySelector('nav[aria-label="Main Navigation"], nav.main-navigation-nav') ?? null;
        footerTargetRef.current = footerRef.current?.querySelector("footer") ?? null;
    }, [showHeader, showFooter, showCompactHeader, pageContent, footer, header, compactHeader]);

    const focusTarget = (target: HTMLElement | null) => {
        if (!target) {
            return;
        }

        if (!target.hasAttribute("tabindex")) {
            target.setAttribute("tabindex", "-1");
        }

        target.focus();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <SiteWrapper {...styles.siteWrapper /* Custom Code */}>
            <StyledWrapper {...styles.skipLinks} aria-label={translate("Skip links")}>
                <SkipNav
                    text={translate("Skip to main content")}
                    overridedStyles={styles.skipLink}
                    focusFunction={() => focusTarget(mainTargetRef.current)}
                />
                {showHeader && (
                    <SkipNav
                        text={translate("Skip to menu")}
                        overridedStyles={styles.skipLink}
                        focusFunction={() => focusTarget(navTargetRef.current)}
                    />
                )}
                {showFooter && (
                    <SkipNav
                        text={translate("Skip to footer")}
                        overridedStyles={styles.skipLink}
                        focusFunction={() => focusTarget(footerTargetRef.current)}
                    />
                )}
            </StyledWrapper>
            {/* Custom Code */}
            {showHeader && (
                <div {...styles.header} ref={headerRef}>
                    {header}
                </div>
            )}
            {/* Custom Code */}
            {showCompactHeader && (
                <div>
                    {compactHeader}
                </div>
            )}
            {showBreadcrumbs && (
                <Page as="div" {...styles.breadcrumb /* Custom Code */}>
                    <PageBreadcrumbs />
                </Page>
            )}
            {/* Custom Code - Start */}
            <div {...styles.content} ref={contentRef}>
                {pageContent}
            </div>
            <ExperimentsSnippets />
            <GoogleAnalytics />
            <FraudProtectionFingerprint />
            {/* Store Custom Code - Start */}
            <LiveChat />
            {/* Store Custom Code - End */}
            {showFooter && (
                <div {...styles.footer} ref={footerRef}>
                    {footer}
                </div>
            )}
            {/* Custom Code - End */}
            {showSignInOverlay && signInOverlay}
            {showStorePickupOverlay && storePickupOverlay}
            {showAddressBookOverlay && addressBookOverlay}
        </SiteWrapper>
    );
};

export default PageLayout;
